import { useEffect, useState } from 'react';

interface Institution {
  institution_id: string;
  name: string;
  type: string;
  category: string;
  tier: string;
  region: string;
  province: string;
  assets: number;
  cet1_ratio: number | null;
  lcr: number | null;
  cyber_incidents_ytd: number;
  supervisory_rating: number;
  trend: string;
}

interface TimeSeriesMetric {
  metric: string;
  period: string;
  value: number;
  target: number;
  warning: number;
  minimum?: number;
  critical_threshold?: number;
}

export default function SupervisoryOverview() {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [metrics, setMetrics] = useState<TimeSeriesMetric[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedTier, setSelectedTier] = useState<string>('all');

  useEffect(() => {
    // Load mock data
    const institutionsData: Institution[] = [
      {
        institution_id: "INS-001",
        name: "Northern Maple Financial",
        type: "Bank",
        category: "Schedule I",
        tier: "Tier 2",
        region: "Ontario",
        province: "ON",
        assets: 45000000000,
        cet1_ratio: 0.048,
        lcr: 1.33,
        cyber_incidents_ytd: 10,
        supervisory_rating: 2,
        trend: "stable"
      },
      {
        institution_id: "INS-002",
        name: "Pacific Coast Bank",
        type: "Bank",
        category: "Schedule I",
        tier: "Tier 2",
        region: "British Columbia",
        province: "BC",
        assets: 52000000000,
        cet1_ratio: 0.052,
        lcr: 1.45,
        cyber_incidents_ytd: 8,
        supervisory_rating: 2,
        trend: "improving"
      },
      {
        institution_id: "INS-003",
        name: "Atlantic Financial Group",
        type: "Bank",
        category: "Schedule I",
        tier: "Tier 3",
        region: "Atlantic",
        province: "NS",
        assets: 18000000000,
        cet1_ratio: 0.046,
        lcr: 1.28,
        cyber_incidents_ytd: 4,
        supervisory_rating: 2,
        trend: "stable"
      },
      {
        institution_id: "INS-004",
        name: "Prairie Credit Union",
        type: "Credit Union",
        category: "Federal",
        tier: "Tier 4",
        region: "Prairies",
        province: "AB",
        assets: 8500000000,
        cet1_ratio: 0.055,
        lcr: 1.52,
        cyber_incidents_ytd: 2,
        supervisory_rating: 1,
        trend: "stable"
      },
      {
        institution_id: "INS-005",
        name: "National Trust Corporation",
        type: "Trust",
        category: "Schedule II",
        tier: "Tier 3",
        region: "Ontario",
        province: "ON",
        assets: 22000000000,
        cet1_ratio: 0.050,
        lcr: 1.38,
        cyber_incidents_ytd: 5,
        supervisory_rating: 2,
        trend: "stable"
      },
      {
        institution_id: "INS-006",
        name: "Quebec Banque Nationale",
        type: "Bank",
        category: "Schedule I",
        tier: "Tier 2",
        region: "Quebec",
        province: "QC",
        assets: 38000000000,
        cet1_ratio: 0.049,
        lcr: 1.35,
        cyber_incidents_ytd: 7,
        supervisory_rating: 2,
        trend: "stable"
      },
      {
        institution_id: "INS-007",
        name: "Metro Insurance Ltd",
        type: "Insurer",
        category: "Life",
        tier: "Tier 3",
        region: "Ontario",
        province: "ON",
        assets: 15000000000,
        cet1_ratio: null,
        lcr: null,
        cyber_incidents_ytd: 3,
        supervisory_rating: 2,
        trend: "stable"
      },
      {
        institution_id: "INS-008",
        name: "Western Digital Bank",
        type: "Bank",
        category: "Schedule I",
        tier: "Tier 4",
        region: "British Columbia",
        province: "BC",
        assets: 5200000000,
        cet1_ratio: 0.058,
        lcr: 1.62,
        cyber_incidents_ytd: 1,
        supervisory_rating: 1,
        trend: "improving"
      },
      {
        institution_id: "INS-009",
        name: "Central Canada Life",
        type: "Insurer",
        category: "Life",
        tier: "Tier 2",
        region: "Ontario",
        province: "ON",
        assets: 42000000000,
        cet1_ratio: null,
        lcr: null,
        cyber_incidents_ytd: 6,
        supervisory_rating: 2,
        trend: "stable"
      },
      {
        institution_id: "INS-010",
        name: "Maritime Trust Company",
        type: "Trust",
        category: "Schedule II",
        tier: "Tier 5",
        region: "Atlantic",
        province: "NB",
        assets: 2800000000,
        cet1_ratio: 0.062,
        lcr: 1.75,
        cyber_incidents_ytd: 1,
        supervisory_rating: 1,
        trend: "stable"
      }
    ];

    const metricsData: TimeSeriesMetric[] = [
      { metric: "system_cet1_ratio", period: "2024-Q4", value: 0.0492, target: 0.045, warning: 0.047, minimum: 0.043 },
      { metric: "system_cet1_ratio", period: "2025-Q1", value: 0.0495, target: 0.045, warning: 0.047, minimum: 0.043 },
      { metric: "system_cet1_ratio", period: "2025-Q2", value: 0.0498, target: 0.045, warning: 0.047, minimum: 0.043 },
      { metric: "system_cet1_ratio", period: "2025-Q3", value: 0.0501, target: 0.045, warning: 0.047, minimum: 0.043 },
      { metric: "system_lcr", period: "2024-Q4", value: 1.38, target: 1.00, warning: 1.10, minimum: 1.00 },
      { metric: "system_lcr", period: "2025-Q1", value: 1.41, target: 1.00, warning: 1.10, minimum: 1.00 },
      { metric: "system_lcr", period: "2025-Q2", value: 1.43, target: 1.00, warning: 1.10, minimum: 1.00 },
      { metric: "system_lcr", period: "2025-Q3", value: 1.45, target: 1.00, warning: 1.10, minimum: 1.00 },
      { metric: "cyber_incidents", period: "2024-Q4", value: 42, target: 0, warning: 50, critical_threshold: 60 },
      { metric: "cyber_incidents", period: "2025-Q1", value: 38, target: 0, warning: 50, critical_threshold: 60 },
      { metric: "cyber_incidents", period: "2025-Q2", value: 45, target: 0, warning: 50, critical_threshold: 60 },
      { metric: "cyber_incidents", period: "2025-Q3", value: 47, target: 0, warning: 50, critical_threshold: 60 },
    ];

    setInstitutions(institutionsData);
    setMetrics(metricsData);
    setLoading(false);
  }, []);

  // Filter logic
  const filteredInstitutions = institutions.filter(inst => {
    if (selectedRegion !== 'all' && inst.region !== selectedRegion) return false;
    if (selectedType !== 'all' && inst.type !== selectedType) return false;
    if (selectedTier !== 'all' && inst.tier !== selectedTier) return false;
    return true;
  });

  // Calculate aggregates
  const totalAssets = filteredInstitutions.reduce((sum, inst) => sum + inst.assets, 0);
  const bankInstitutions = filteredInstitutions.filter(i => i.cet1_ratio !== null);
  const avgCET1 = bankInstitutions.length > 0
    ? bankInstitutions.reduce((sum, inst) => sum + (inst.cet1_ratio || 0), 0) / bankInstitutions.length
    : 0;
  const avgLCR = bankInstitutions.length > 0
    ? bankInstitutions.reduce((sum, inst) => sum + (inst.lcr || 0), 0) / bankInstitutions.length
    : 0;
  const totalCyberIncidents = filteredInstitutions.reduce((sum, inst) => sum + inst.cyber_incidents_ytd, 0);

  // Get time series for system metrics
  const getMetricTimeSeries = (metricName: string) => {
    return metrics.filter(m => m.metric === metricName).sort((a, b) => a.period.localeCompare(b.period));
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">System-Wide Supervisory Overview</h2>
        <p className="text-sm text-gray-600 mt-1">
          Monitoring {institutions.length} federally regulated financial institutions across Canada
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Region</label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-700"
            >
              <option value="all">All Regions</option>
              <option value="Ontario">Ontario</option>
              <option value="Quebec">Quebec</option>
              <option value="British Columbia">British Columbia</option>
              <option value="Prairies">Prairies</option>
              <option value="Atlantic">Atlantic</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-700"
            >
              <option value="all">All Types</option>
              <option value="Bank">Bank</option>
              <option value="Credit Union">Credit Union</option>
              <option value="Trust">Trust</option>
              <option value="Insurer">Insurer</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Tier</label>
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-700"
            >
              <option value="all">All Tiers</option>
              <option value="Tier 1">Tier 1</option>
              <option value="Tier 2">Tier 2</option>
              <option value="Tier 3">Tier 3</option>
              <option value="Tier 4">Tier 4</option>
              <option value="Tier 5">Tier 5</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSelectedRegion('all');
                setSelectedType('all');
                setSelectedTier('all');
              }}
              className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Aggregate Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Institutions"
          value={filteredInstitutions.length.toString()}
          subtitle={`of ${institutions.length} monitored`}
          icon="🏛️"
        />
        <MetricCard
          title="Total Assets"
          value={`$${(totalAssets / 1000000000).toFixed(0)}B`}
          subtitle="Combined supervised assets"
          icon="💰"
        />
        <MetricCard
          title="Avg CET1 Ratio"
          value={`${(avgCET1 * 100).toFixed(1)}%`}
          subtitle="Banking sector average"
          icon="📊"
          sparkline={getMetricTimeSeries('system_cet1_ratio')}
        />
        <MetricCard
          title="Cyber Incidents YTD"
          value={totalCyberIncidents.toString()}
          subtitle="Across all institutions"
          icon="🔒"
        />
      </div>

      {/* System-Wide Metrics with Bullet Charts */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System-Wide Prudential Metrics</h3>
        <div className="space-y-6">
          <BulletChart
            label="CET1 Capital Ratio"
            current={(avgCET1 * 100).toFixed(1)}
            target={4.5}
            warning={4.7}
            minimum={4.3}
            unit="%"
            timeseries={getMetricTimeSeries('system_cet1_ratio')}
          />
          <BulletChart
            label="Liquidity Coverage Ratio (LCR)"
            current={(avgLCR * 100).toFixed(0)}
            target={100}
            warning={110}
            minimum={100}
            unit="%"
            timeseries={getMetricTimeSeries('system_lcr')}
          />
          <BulletChart
            label="System Cyber Incidents (Quarterly)"
            current={getMetricTimeSeries('cyber_incidents')[3]?.value.toString() || "0"}
            target={0}
            warning={50}
            critical={60}
            unit=""
            timeseries={getMetricTimeSeries('cyber_incidents')}
            reverse={true}
          />
        </div>
      </div>

      {/* Institutions Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Institution
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Type / Tier
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Region
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Assets
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                  CET1
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                  LCR
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Cyber YTD
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Trend
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInstitutions.map((inst) => (
                <tr key={inst.institution_id} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{inst.name}</div>
                    <div className="text-xs text-gray-500">{inst.institution_id}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{inst.type}</div>
                    <div className="text-xs text-gray-500">{inst.tier}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {inst.province}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                    ${(inst.assets / 1000000000).toFixed(1)}B
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {inst.cet1_ratio !== null ? (
                      <span className={`text-sm font-semibold ${
                        inst.cet1_ratio >= 0.047 ? 'text-green-600' :
                        inst.cet1_ratio >= 0.045 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {(inst.cet1_ratio * 100).toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {inst.lcr !== null ? (
                      <span className={`text-sm font-semibold ${
                        inst.lcr >= 1.10 ? 'text-green-600' :
                        inst.lcr >= 1.00 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {(inst.lcr * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <span className={`text-sm font-semibold ${
                      inst.cyber_incidents_ytd >= 10 ? 'text-red-600' :
                      inst.cyber_incidents_ytd >= 5 ? 'text-yellow-600' :
                      'text-green-600'
                    }`}>
                      {inst.cyber_incidents_ytd}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      inst.supervisory_rating === 1 ? 'bg-green-100 text-green-800' :
                      inst.supervisory_rating === 2 ? 'bg-blue-100 text-blue-800' :
                      inst.supervisory_rating === 3 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {inst.supervisory_rating}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <span className="text-xs">
                      {inst.trend === 'improving' ? '📈' :
                       inst.trend === 'declining' ? '📉' : '➡️'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Risk Radar Chart */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Risk Assessment</h3>
        <div className="flex justify-center">
          <RadarChart />
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, subtitle, icon, sparkline }: any) {
  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <p className="text-xs text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
      {sparkline && <Sparkline data={sparkline} />}
    </div>
  );
}

function Sparkline({ data }: { data: TimeSeriesMetric[] }) {
  if (!data || data.length === 0) return null;

  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((d.value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="mt-2">
      <svg width="100%" height="30" viewBox="0 0 100 100" preserveAspectRatio="none">
        <polyline
          points={points}
          fill="none"
          stroke="#003366"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

function BulletChart({ label, current, target, warning, minimum, critical, unit, timeseries, reverse = false }: any) {
  const currentNum = parseFloat(current);
  const maxValue = critical ? parseFloat(critical) * 1.2 : Math.max(parseFloat(target), parseFloat(warning || target), currentNum) * 1.3;

  const getPosition = (value: number) => {
    return (value / maxValue) * 100;
  };

  const getStatus = () => {
    if (reverse) {
      // For reverse metrics (lower is better), like cyber incidents
      if (critical && currentNum >= parseFloat(critical)) return 'critical';
      if (warning && currentNum >= parseFloat(warning)) return 'warning';
      return 'good';
    } else {
      // For normal metrics (higher is better)
      if (minimum && currentNum < parseFloat(minimum)) return 'critical';
      if (currentNum < parseFloat(target)) return 'warning';
      return 'good';
    }
  };

  const status = getStatus();

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex items-center space-x-4 text-xs text-gray-600">
          <span>Current: <strong className={
            status === 'critical' ? 'text-red-600' :
            status === 'warning' ? 'text-yellow-600' :
            'text-green-600'
          }>{current}{unit}</strong></span>
          <span>Target: {target}{unit}</span>
        </div>
      </div>
      <div className="relative h-8">
        {/* Background zones */}
        {!reverse && minimum && (
          <div
            className="absolute h-8 bg-red-100"
            style={{ left: 0, width: `${getPosition(parseFloat(minimum))}%` }}
          />
        )}
        <div
          className={`absolute h-8 ${reverse ? 'bg-green-100' : 'bg-yellow-100'}`}
          style={{
            left: !reverse && minimum ? `${getPosition(parseFloat(minimum))}%` : 0,
            width: `${getPosition(parseFloat(warning || target)) - (minimum && !reverse ? getPosition(parseFloat(minimum)) : 0)}%`
          }}
        />
        <div
          className={`absolute h-8 ${reverse ? 'bg-yellow-100' : 'bg-green-100'}`}
          style={{
            left: `${getPosition(parseFloat(warning || target))}%`,
            width: critical
              ? `${getPosition(parseFloat(critical)) - getPosition(parseFloat(warning || target))}%`
              : `${100 - getPosition(parseFloat(warning || target))}%`
          }}
        />
        {reverse && critical && (
          <div
            className="absolute h-8 bg-red-100"
            style={{
              left: `${getPosition(parseFloat(critical))}%`,
              width: `${100 - getPosition(parseFloat(critical))}%`
            }}
          />
        )}

        {/* Current value marker */}
        <div
          className="absolute top-0 h-8 w-1 bg-blue-700 z-10"
          style={{ left: `${getPosition(currentNum)}%` }}
        />
        <div
          className="absolute -top-1 h-10 w-3 border-2 border-blue-700 bg-white z-10"
          style={{ left: `${getPosition(currentNum) - 1}%` }}
        />
      </div>
      {timeseries && (
        <div className="mt-2">
          <Sparkline data={timeseries} />
        </div>
      )}
    </div>
  );
}

function RadarChart() {
  // Risk dimensions: Capital, Liquidity, Cyber, Operational, Model Risk
  const dimensions = [
    { label: 'Capital Adequacy', score: 85 },
    { label: 'Liquidity', score: 90 },
    { label: 'Cyber Resilience', score: 72 },
    { label: 'Operational Risk', score: 78 },
    { label: 'Model Risk Mgmt', score: 68 },
    { label: 'Third-Party Risk', score: 75 },
  ];

  const centerX = 150;
  const centerY = 150;
  const radius = 100;
  const numPoints = dimensions.length;

  // Calculate polygon points
  const points = dimensions.map((dim, i) => {
    const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
    const score = dim.score / 100;
    const x = centerX + Math.cos(angle) * radius * score;
    const y = centerY + Math.sin(angle) * radius * score;
    return `${x},${y}`;
  }).join(' ');

  // Calculate axis endpoints
  const axes = dimensions.map((dim, i) => {
    const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
    const x = centerX + Math.cos(angle) * radius;
    const y = centerY + Math.sin(angle) * radius;
    const labelX = centerX + Math.cos(angle) * (radius + 25);
    const labelY = centerY + Math.sin(angle) * (radius + 25);
    return { x, y, labelX, labelY, label: dim.label, score: dim.score };
  });

  return (
    <svg width="400" height="400" viewBox="0 0 300 300">
      {/* Background circles */}
      {[0.2, 0.4, 0.6, 0.8, 1.0].map((scale) => (
        <circle
          key={scale}
          cx={centerX}
          cy={centerY}
          r={radius * scale}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}

      {/* Axes */}
      {axes.map((axis, i) => (
        <g key={i}>
          <line
            x1={centerX}
            y1={centerY}
            x2={axis.x}
            y2={axis.y}
            stroke="#d1d5db"
            strokeWidth="1"
          />
          <text
            x={axis.labelX}
            y={axis.labelY}
            textAnchor="middle"
            className="text-xs"
            fill="#374151"
            fontSize="10"
          >
            {axis.label}
          </text>
          <text
            x={axis.labelX}
            y={axis.labelY + 12}
            textAnchor="middle"
            className="text-xs font-semibold"
            fill="#003366"
            fontSize="11"
          >
            {axis.score}
          </text>
        </g>
      ))}

      {/* Data polygon */}
      <polygon
        points={points}
        fill="rgba(0, 51, 102, 0.3)"
        stroke="#003366"
        strokeWidth="2"
      />

      {/* Data points */}
      {dimensions.map((dim, i) => {
        const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
        const score = dim.score / 100;
        const x = centerX + Math.cos(angle) * radius * score;
        const y = centerY + Math.sin(angle) * radius * score;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="4"
            fill="#003366"
          />
        );
      })}
    </svg>
  );
}
